import { ReactNode } from 'react';

/** A single panel in the side-section stack: a key and its mounted node. */
export type PanelLayer = {
  key: string;
  node: ReactNode;
};

export const keysOf = (layers: PanelLayer[]): string[] =>
  layers.map((layer) => layer.key);

/** Normalize the `activePanel` selector (a key, a stack of keys, or nothing). */
export const normalizeStack = (activePanel?: string | string[]): string[] =>
  activePanel === undefined ? [] : [activePanel].flat();

/**
 * Dedupe keys keeping each key's *last* occurrence: a consecutive repeat is a
 * no-op (the top is unchanged), while a key reappearing behind others moves to
 * the top and its earlier instance is dropped.
 */
export const dedupeKeepingLastOccurrence = (keys: string[]): string[] =>
  keys.filter((key, index) => keys.lastIndexOf(key) === index);

/**
 * Reconcile the layers animating out after a structural change: drop any key
 * that was re-pushed into the stack, begin exiting the newly popped keys (with
 * the node captured from the previous stack), and skip keys already exiting.
 * Returns the previous array unchanged when nothing changed, so a pure reorder
 * does not trigger a re-render.
 */
export const reconcileExitingLayers = (
  exiting: PanelLayer[],
  removed: PanelLayer[],
  nextKeys: string[]
): PanelLayer[] => {
  const stillExiting = exiting.filter((layer) => !nextKeys.includes(layer.key));
  const additions = removed.filter(
    (layer) => !stillExiting.some((existing) => existing.key === layer.key)
  );

  return additions.length === 0 && stillExiting.length === exiting.length
    ? exiting
    : [...stillExiting, ...additions];
};
